import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "../auth/AuthContext";
import { CSV_UPLOAD_ACCEPT, getCsvFileError } from "../utils/uploadValidation";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

export default function SonarUploadPage() {
    const { isAuthenticated, ready } = useAuth();
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("error");
    const [stats, setStats] = useState(null);

    function onFileChange(event) {
        const nextFile = event.target.files?.[0] || null;
        const fileError = nextFile ? getCsvFileError(nextFile) : "";
        setStats(null);
        if (fileError) {
            setFile(null);
            setMessage(fileError);
            setMessageType("error");
            event.target.value = "";
            return;
        }

        setFile(nextFile);
        setMessage("");
    }

    const onUpload = async (event) => {
        event.preventDefault();
        const fileError = getCsvFileError(file);
        if (fileError) {
            setMessage(fileError);
            setMessageType("error");
            return;
        }
        setLoading(true);
        setMessage("");
        setStats(null);
        const formData = new FormData();
        formData.append("file", file);
        try {
            const response = await axios.post(`${API_BASE_URL}/api/depth/upload`, formData, { withCredentials: true });
            setStats(response.data || null);
            setMessage("CSV успешно обработан.");
            setMessageType("success");
        } catch (error) {
            setMessage(error?.response?.data?.error || "Не удалось загрузить CSV.");
            setMessageType("error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="blog-page-root">
            <div className="blog-page-inner fish-detection-inner">

                <div className="blog-page-header">
                    <div className="blog-head">
                        <h2 className="blog-page-title">Загрузка точек эхолота</h2>
                    </div>
                </div>

                <div className="cms-page-panel">
                    <div className="rich-content cms-page-content sonar-instructions">
                        <h3>Как подготовить CSV с точками</h3>

                        <h4>Синхронизация данных с эхолота в телефон</h4>
                        <ol>
                            <li>Убедитесь, что эхолот подключен к телефону через Wi-Fi, и вы провели сканирование в режиме «Берег GPS» или «Лодка».</li>
                            <li>Откройте приложение Fish Deeper.</li>
                            <li>Перейдите в раздел «История».</li>
                            <li>Нажмите на оранжевую иконку «облако» в правом верхнем углу, чтобы загрузить данные в облако.</li>
                        </ol>

                        <h4>Экспорт точек через Lakebook на компьютере</h4>
                        <ol>
                            <li>Для работы с точками на ПК используйте платформу Deeper Lakebook</li>
                            <li>Перейдите на сайт <a href="https://fishdeeper.com" target="_blank" rel="noreferrer">fishdeeper.com</a> и авторизуйтесь под своим аккаунтом.</li>
                            <li>В меню выберите «Карты» или «Точки интереса».</li>
                            <li>Нажмите кнопку «Экспорт» и сохраните данные в формате CSV.</li>
                        </ol>

                        <h4>Экспорт через мобильное приложение</h4>
                        <ol>
                            <li>Откройте приложение Fish Deeper.</li>
                            <li>Зайдите в «Историю».</li>
                            <li>Выберите нужную сессию сканирования.</li>
                            <li>Нажмите на три точки («меню») и выберите «Экспорт».</li>
                        </ol>
                    </div>
                </div>

                {!ready && (
                    <div className="cms-page-panel margin-top-16 page-auth-panel">
                        <p className="cms-page-loading">Проверяем авторизацию...</p>
                    </div>
                )}

                {ready && !isAuthenticated && (
                    <div className="cms-page-panel margin-top-16 page-auth-panel">
                        <h2 className="cms-page-title">Необходима авторизация</h2>
                        <p className="page-status-message">Войдите в аккаунт, чтобы загрузить CSV с точками эхолота.</p>
                    </div>
                )}

                {ready && isAuthenticated && (
                    <div className="cms-page-panel margin-top-16">
                        <p className="fish-detection-section-label margin-bottom-16">Загрузить файл</p>
                        <form onSubmit={onUpload}>
                            <div className="fish-detection-form-inner">
                                <label className="btn-outline fish-detection-form-button">
                                    <span>{file ? file.name : "Файл данных эхолота"}</span>
                                    <input
                                        type="file"
                                        accept={CSV_UPLOAD_ACCEPT}
                                        onChange={onFileChange}
                                        className="display-none"
                                    />
                                </label>
                                <button className="btn-outline" type="submit" disabled={loading || !file}>
                                    {loading ? "Загружаем..." : "Загрузить CSV"}
                                </button>
                            </div>
                        </form>
                        {!!message && (
                            <p className={`form-message${messageType === "success" ? " form-message--success" : ""} margin-top-16`}>
                                {message}
                            </p>
                        )}
                    </div>
                )}

                {!!stats && (
                    <div className="cms-page-panel margin-top-16">
                        <p className="fish-detection-section-label margin-bottom-16">Результат обработки</p>
                        <div className="sonar-stats-grid">
                            <div className="sonar-stat-item">
                                <span className="sonar-stat-value">{stats.inserted ?? 0}</span>
                                <span className="sonar-stat-label">Загружено</span>
                            </div>
                            <div className="sonar-stat-item">
                                <span className="sonar-stat-value">{stats.skipped ?? 0}</span>
                                <span className="sonar-stat-label">Пропущено</span>
                            </div>
                            <div className="sonar-stat-item">
                                <span className="sonar-stat-value">{stats.skippedEmptyCoordinates ?? 0}</span>
                                <span className="sonar-stat-label">Пустые координаты</span>
                            </div>
                            <div className="sonar-stat-item">
                                <span className="sonar-stat-value">{stats.processedLines ?? 0}</span>
                                <span className="sonar-stat-label">Всего строк</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
